import { WrapperEntitySelector } from '@components/Inputs';
import { generateSearchEntitySelector } from '@modules/entitySelector/entitySelector.utils';
import { Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { DateTimeInput, Grid, Icon, IconButton, Select, Switch, TextInput } from '@ts/storybook';
import { CStatusIndicator } from '@ts/ui-models';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { IStatusInputProps } from './StatusInput';
import { sortIndicatorsByName } from './utils/statusIndicator.utils';

interface IStatusFormProps extends IStatusInputProps {
  indicators: CStatusIndicator[];
  renderUpdate: (indicator: CStatusIndicator) => React.ReactNode;
}

const StatusInputEditMode: React.FC<IStatusFormProps> = ({
  indicators,
  onChangeList,
  fAllowAdd = true,
  showUpdate,
  renderUpdate,
}) => {
  const { t } = useTranslation();

  const sortedIndicators = useMemo(() => sortIndicatorsByName(indicators), [indicators]);

  const handleValueListChange = (indIdx: number, value: any) => {
    const newList = sortedIndicators.map((ind, idx) => {
      if (indIdx === idx) {
        ind.value = value;
      }
      return ind;
    });
    onChangeList(newList);
  };

  const handleAddIndicator = (indicator: CStatusIndicator | null) => {
    if (!indicator) return;

    const newList = [...indicators, indicator];
    onChangeList(newList);
  };

  const handleRemoveIndicator = (indIdx: number) => {
    const newList = sortedIndicators.filter((_, idx) => idx !== indIdx);
    onChangeList(newList);
  };

  const renderEditValue = (indicator: CStatusIndicator, indIdx: number) => {
    const manualEnabled = !!indicator.update?.manual;

    switch (indicator.type) {
      case 'boolean':
        return (
          <Switch
            key={indIdx}
            checked={indicator.getTypedValue('boolean') ?? false}
            onChangeValue={value => handleValueListChange(indIdx, value)}
            leftLabel="False"
            rightLabel="True"
            disabled={!manualEnabled}
          />
        );
      case 'list': {
        const pvs = indicator.possibleValues ?? [];
        const menuItems = pvs.map((pv, pvIdx) => ({
          key: `${pv}-${pvIdx}`,
          value: pv,
          label: `${pv}`,
        }));

        const currentValue = indicator.value ?? pvs[0];

        return (
          <Select
            key={indIdx}
            value={String(currentValue)}
            onChange={value => handleValueListChange(indIdx, value)}
            disabled={!manualEnabled}
            options={menuItems}
          />
        );
      }
      case 'string':
        return (
          <TextInput
            key={indIdx}
            disabled={!manualEnabled}
            placeholder="Value"
            value={indicator.getTypedValue('string')}
            onChangeValue={value => handleValueListChange(indIdx, value)}
          />
        );
      case 'integer':
      case 'float':
      case 'distance':
      case 'speed':
        return (
          <TextInput
            type="number"
            key={indIdx}
            disabled={!manualEnabled}
            placeholder="Value"
            value={indicator.getTypedValue('integer')}
            onChangeValue={value => handleValueListChange(indIdx, value)}
          />
        );
      case 'date':
      case 'time':
      case 'timestamp':
        return (
          <DateTimeInput
            onlyDate
            label="date"
            disabled={!manualEnabled}
            key={indIdx}
            value={indicator.getTypedValue('date')}
            onChange={value => handleValueListChange(indIdx, value)}
            onlyTime={indicator.type === 'time'}
          />
        );

      default:
        return null;
    }
  };

  const renderAddButton = () => {
    if (!fAllowAdd) return null;

    return (
      <WrapperEntitySelector
        showAsButton={onClick => (
          <IconButton tooltip="New status indicator" sx={{ zIndex: 5 }} onClick={onClick}>
            <Icon icon="addBox" size={25} />
          </IconButton>
        )}
        searchComponent={generateSearchEntitySelector('indicatorType')}
        onChangeEntity={value => handleAddIndicator(value as CStatusIndicator)}
      />
    );
  };

  return (
    <Grid container alignItems="flex-start" size={12}>
      {renderAddButton()}
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('Indicator')}</TableCell>
            <TableCell>{t('Value')}</TableCell>
            <TableCell>{t('Unit')}</TableCell>
            {showUpdate && <TableCell>{t('Update')}</TableCell>}
            <TableCell>{t('Delete')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedIndicators.map((indicator, indIdx) => {
            const unit = indicator.unit?.abbreviation ?? null;
            return (
              <TableRow key={(indicator.logicalId || indicator.id || indIdx).toString()}>
                <TableCell>{indicator.name}</TableCell>
                <TableCell>{renderEditValue(indicator, indIdx)}</TableCell>
                <TableCell>{unit}</TableCell>
                {showUpdate && <TableCell>{renderUpdate(indicator)}</TableCell>}
                <TableCell>
                  <IconButton
                    id="delete"
                    tooltip="Delete"
                    icon="delete"
                    stProps={{
                      icon: {
                        icon: { name: 'delete', color: 'error' },
                      },
                    }}
                    onClick={() => handleRemoveIndicator(indIdx)}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Grid>
  );
};

export default StatusInputEditMode;
